import styles from "./AppDialog.module.scss";
import { Dialog } from "primereact/dialog";
import { classNames } from "primereact/utils";
import { AppTooltip } from "../tooltip/AppTooltip";
import { Icon, IconType } from "../../icons/Icon";
import { RenderWhen } from "../../infrastructure/components/RenderWhen";
import { dialogTransitionOptions } from "./dialogTransition";
import { useLastWhileVisible } from "./hooks/useLastWhileVisible";

interface IAppDialogProps {
  header?: string | JSX.Element;
  headerIconType?: IconType;
  headerIconColor?: string;
  visible: boolean;
  onHide: () => void;
  style?: React.CSSProperties;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  /** Class for the fixed backdrop the dialog is centred in (e.g. to top-align it while a keyboard is up). */
  maskClassName?: string;
  showHeader?: boolean;
  children: React.ReactNode;
  tooltip?: string;
  closable?: boolean;
  /**
   * Whether clicking the backdrop closes the dialog. Defaults to following `closable`.
   * Set to false on dialogs holding work the user would hate to lose to a stray click.
   */
  dismissableMask?: boolean;
  /** Whether Escape closes the dialog. Defaults to following `closable`. */
  closeOnEscape?: boolean;
  /**
   * Id of the element that names the dialog, for a dialog without a header (PrimeReact
   * otherwise points screen readers at the header it does not render).
   */
  ariaLabelledBy?: string;
}

export const AppDialog = ({
  header,
  headerIconType,
  headerIconColor = "rgba(255, 255, 255, 0.95)",
  visible,
  onHide,
  style,
  className,
  headerClassName,
  contentClassName,
  maskClassName,
  showHeader = true,
  children,
  tooltip,
  closable = true,
  dismissableMask,
  closeOnEscape,
  ariaLabelledBy,
}: IAppDialogProps) => {
  // Shown unchanged while the dialog animates out, even if the caller already cleared them.
  const shownHeader = useLastWhileVisible(visible, header);
  const shownChildren = useLastWhileVisible(visible, children);

  header = (
    <span className={styles.dialog_header_contents}>
      <RenderWhen condition={!!headerIconType}>
        <Icon type={headerIconType!} color={headerIconColor} />
      </RenderWhen>
      {shownHeader}{" "}
      <AppTooltip
        className={styles.dialog_header_contents_tooltip}
        content={tooltip}
      />
    </span>
  );
  return (
    <Dialog
      header={header}
      visible={visible}
      onHide={onHide}
      style={style}
      className={classNames(className, styles.dialog)}
      headerClassName={classNames(headerClassName, styles.dialog_header)}
      contentClassName={classNames(contentClassName, styles.dialog_content)}
      maskClassName={maskClassName}
      closable={closable}
      closeOnEscape={closeOnEscape ?? !!closable}
      dismissableMask={dismissableMask ?? !!closable}
      showHeader={showHeader}
      transitionOptions={dialogTransitionOptions}
      modal
      {...(ariaLabelledBy && { "aria-labelledby": ariaLabelledBy })}
    >
      {shownChildren}
    </Dialog>
  );
};
